import posthog from 'posthog-js';
import type { SubscriberIdentity } from './identity';

type AnalyticsProperties = Record<string, boolean | number | string | null | undefined>;

export type AnalyticsEvent =
  | 'feedback_opened'
  | 'feedback_submitted'
  | 'newsletter_signup_clicked'
  | 'recording_access_clicked'
  | 'recording_opened_external'
  | 'recording_play_completed'
  | 'recording_play_started'
  | 'recording_player_loaded'
  | 'sign_in_started'
  | 'signed_out'
  | 'subscription_status_refresh_started'
  | 'workshop_registration_completed'
  | 'workshop_registration_failed'
  | 'workshop_registration_sign_in_required'
  | 'workshop_registration_started'
  | 'workshop_unregistered'
  | 'workshop_viewed';

let initialized = false;
let identifiedId: string | null = null;
let identifiedTraits: string | null = null;
let identityGeneration = 0;

/**
 * Start product analytics only when a PostHog project key is configured.
 * Replay is deliberately sampled and all form input values are masked.
 */
export function initAnalytics(): void {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!apiKey || initialized) return;

  const replaySampleRate = parseReplaySampleRate(
    import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE,
  );

  posthog.init(apiKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com',
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_performance: false,
    person_profiles: 'identified_only',
    respect_dnt: true,
    disable_session_recording: isSensitivePath(window.location.pathname),
    session_recording: {
      sampleRate: replaySampleRate,
      maskAllInputs: true,
      maskTextSelector: '[data-private]',
      blockSelector: '[data-posthog-block]',
      recordHeaders: false,
      recordBody: false,
      captureCanvas: { recordCanvas: false },
      // Keep network payloads out of replays even if a remote project setting
      // later enables request capture.
      maskCapturedNetworkRequestFn: () => null,
    },
  });

  initialized = true;
}

export function capture(event: AnalyticsEvent, properties?: AnalyticsProperties): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/**
 * Connect sessions for the same subscriber without sending their email or name
 * to PostHog. The remaining traits are coarse entitlement metadata only.
 */
export async function identifySubscriber(identity: SubscriberIdentity): Promise<void> {
  if (!initialized) return;

  const generation = identityGeneration;
  const hash = await sha256(identity.email.trim().toLowerCase());
  if (!hash || generation !== identityGeneration) return;

  const id = `subscriber_${hash}`;
  const traits = JSON.stringify([
    identity.isSubscriber,
    identity.subscriptionTier,
    identity.subscriptionInterval,
  ]);
  if (id === identifiedId && traits === identifiedTraits) return;

  posthog.identify(id, {
    is_subscriber: identity.isSubscriber,
    subscription_tier: identity.subscriptionTier ?? 'unknown',
    subscription_interval: identity.subscriptionInterval ?? 'unknown',
  });
  identifiedId = id;
  identifiedTraits = traits;
}

export function resetAnalytics(): void {
  if (!initialized) return;
  identityGeneration += 1;
  posthog.reset();
  identifiedId = null;
  identifiedTraits = null;
}

/** Do not record pages that can contain admin data or private discount codes. */
export function syncSessionReplay(pathname: string): void {
  if (!initialized) return;
  if (isSensitivePath(pathname)) posthog.stopSessionRecording();
  else posthog.startSessionRecording();
}

export function parseReplaySampleRate(value: string | undefined): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0.1;
  return Math.min(1, Math.max(0, parsed));
}

function isSensitivePath(pathname: string): boolean {
  return pathname.startsWith('/admin') || pathname === '/course-discount';
}

async function sha256(value: string): Promise<string | null> {
  if (!window.crypto?.subtle) return null;
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
